import { MimshakData } from "../interfaces/DTO/mimshakData";
import { BeneficiaryInfo, InsuranceType } from "../interfaces/DTO/mislaka_xml_insurence_models";
import { MimshakPensionService } from "./mimshakPensionService";

export interface BeneficiaryMetrics {
  beneficiaryTotals: { [key: string]: number };
  beneficiaryDeathCoverageTotals: { [key: string]: number };
  totalWithoutBeneficiaries: number;
  totalWithoutBeneficiariesDeathCoverage: number;
  totalLegalHeirs: number;
  totalLegalHeirsDeathCoverage: number;
}

/**
 * מחשב מטריקות מוטבים עבור MimshakData
 * @param mimshakData - נתוני המימשק
 * @param distinctBeneficiaries - רשימת מוטבים ייחודיים (ללא יורשים חוקיים)
 * @returns אובייקט עם כל המטריקות
 */
export function calculateBeneficiariesMetrics(
  mimshakData: MimshakData[],
  distinctBeneficiaries: BeneficiaryInfo[]
): BeneficiaryMetrics {
  const beneficiaryTotals: { [key: string]: number } = {};
  const beneficiaryDeathCoverageTotals: { [key: string]: number } = {};
  let totalWithoutBeneficiaries = 0;
  let totalWithoutBeneficiariesDeathCoverage = 0;
  let totalLegalHeirs = 0;
  let totalLegalHeirsDeathCoverage = 0;

  mimshakData.forEach((item) => {
    const products = item.data?.products || [];
    const filteredProducts = MimshakPensionService.filterInsuranceCandidates(products);

    filteredProducts.forEach((product) =>
      product.policies_or_accounts.forEach((policyOrAccount) => {
        const productBeneficiaries = (policyOrAccount.insurence_coverages?.flatMap(
          coverage => coverage.beneficiaries
        ) || []).filter(b => !MimshakPensionService.isJunkBeneficiary(b));

        // השתמש ב-funds_sum מה-MimshakData
        const fundSum = item.funds_sum || 0;

        // חישוב סכום כיסוי למקרה מוות - חד פעמי
        let deathCoverageAmount = 0;
        const coverages = policyOrAccount.insurence_coverages || [];
        const updateDate = policyOrAccount.update_date;

        coverages.forEach(cov => {
          const details = cov.coverage_details;
          if (!details) return;

          // סינון כיסויים שפגו תוקף - לא נכלול אותם בסכום הביטוח
          const paymentStopDate = details.insurance_fees?.payment_stop_date;
          if (paymentStopDate && updateDate && new Date(paymentStopDate) < new Date(updateDate)) {
            return; // כיסוי פג תוקף - דלג עליו
          }

          const kind = details.insurance_kind;
          const amount = details.insurance_sum ?? 0;

          // אם זה DEATH או ACCIDENTAL_DEATH - קח את הסכום כמו שהוא
          if (kind === InsuranceType.DEATH || kind === InsuranceType.ACCIDENTAL_DEATH) {
            deathCoverageAmount += amount;
          }
        });

        // בדיקה אם יש יורשים חוקיים במוצר (identification_type = 3)
        const hasLegalHeirs = productBeneficiaries.some(b => b.identification_type === 3);
        const legalHeirsBeneficiary = productBeneficiaries.find(b => b.identification_type === 3);

        // בדיקה אם יש מוטבים במוצר (לא כולל יורשים חוקיים)
        const hasBeneficiaries = productBeneficiaries.some(b => b.identification_type !== 3);

        // חישוב סך אחוזי המוטבים (לא כולל יורשים חוקיים)
        const totalBeneficiaryPercentage = productBeneficiaries
          .filter(b => b.identification_type !== 3)
          .reduce((sum, b) => sum + (b.beneficiary_percentage || 0), 0);

        // חישוב אחוז יורשים חוקיים
        const legalHeirsPercentage = legalHeirsBeneficiary?.beneficiary_percentage || 0;

        // סכום כולל של אחוזים (כולל יורשים חוקיים)
        const totalPercentageIncludingLegalHeirs = totalBeneficiaryPercentage + legalHeirsPercentage;

        if (!hasBeneficiaries && !hasLegalHeirs) {
          // אין שום מוטבים - הכל ללא מוטבים
          totalWithoutBeneficiaries += fundSum;
          totalWithoutBeneficiariesDeathCoverage += deathCoverageAmount;
        } else if (totalPercentageIncludingLegalHeirs < 100) {
          // יש מוטבים אבל האחוזים לא מסתכמים ל-100%
          const missingPercentage = 100 - totalPercentageIncludingLegalHeirs;
          totalWithoutBeneficiaries += fundSum * (missingPercentage / 100);
          totalWithoutBeneficiariesDeathCoverage += deathCoverageAmount * (missingPercentage / 100);
        }

        // חישוב סכום ליורשים חוקיים
        if (hasLegalHeirs && legalHeirsBeneficiary?.beneficiary_percentage) {
          const legalHeirsAmount = fundSum * (legalHeirsBeneficiary.beneficiary_percentage / 100);
          const legalHeirsDeathAmount = deathCoverageAmount * (legalHeirsBeneficiary.beneficiary_percentage / 100);

          totalLegalHeirs += legalHeirsAmount;
          totalLegalHeirsDeathCoverage += legalHeirsDeathAmount;
        }

        // חישוב סכום כולל לכל מוטב (לא כולל יורשים חוקיים)
        distinctBeneficiaries.forEach(beneficiary => {
          const matchingBeneficiary = productBeneficiaries.find(
            pb => pb.id_number === beneficiary.id_number
          );

          // חישוב סכום כולל לכל מוטב (אחוז כפול סכום)
          // אבל לא למוטבים למקרה חיים (beneficiary_nature = 1)
          if (matchingBeneficiary?.beneficiary_percentage && matchingBeneficiary?.beneficiary_nature !== 1) {
            const beneficiaryKey = beneficiary.id_number || "";

            // חישוב סכום מחיסכון (fundSum)
            if (fundSum > 0) {
              const beneficiaryAmount = fundSum * (matchingBeneficiary.beneficiary_percentage / 100);
              beneficiaryTotals[beneficiaryKey] = (beneficiaryTotals[beneficiaryKey] || 0) + beneficiaryAmount;
            }

            // חישוב חלק המוטב בסכום ביטוח (death coverage)
            if (deathCoverageAmount > 0) {
              const beneficiaryDeathAmount = deathCoverageAmount * (matchingBeneficiary.beneficiary_percentage / 100);
              beneficiaryDeathCoverageTotals[beneficiaryKey] = (beneficiaryDeathCoverageTotals[beneficiaryKey] || 0) + beneficiaryDeathAmount;
            }
          }
        });
      })
    );
  });

  return {
    beneficiaryTotals,
    beneficiaryDeathCoverageTotals,
    totalWithoutBeneficiaries,
    totalWithoutBeneficiariesDeathCoverage,
    totalLegalHeirs,
    totalLegalHeirsDeathCoverage
  };
}
